import { Suspense } from 'react';
import type { FC, ReactNode } from 'react';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { O11Y_PORTAL_ID } from '@gonfalon/observability-ui/constants';
import { VegaProjectProvider } from '@gonfalon/observability-ui/hooks/VegaProjectProvider';
import { getProjectsQuery } from '@gonfalon/rest-api/queries/projects';
import {
  AgentInsightsPanel,
  type AgentInsightsPanelProps,
} from '@gonfalon/vega/components/AgentInsights/AgentInsightsPanel';
import { useSuspenseQuery } from '@tanstack/react-query';

export interface AccountVegaPanelProps extends Pick<
  AgentInsightsPanelProps,
  'agentPromptMutation' | 'sending' | 'enableAgentResponseSelector'
> {
  children: ReactNode;
}

/**
 * Account-scoped variant of `AgentInsightsPanel`. Used by skill panels
 * (e.g. the pricing concierge) that operate against account-level data
 * rather than a single project.
 *
 * Differences from the project-scoped panel:
 *   - Resolves the account's oldest project as a host so `useProjectId`
 *     has something to bind to when no project route is in scope.
 *   - Marks the panel as a non-gen-AI skill flow, which bypasses the
 *     `isGeneralGenAiEnabled` gate / `AiAccessModal` redirect and
 *     renders a simplified prompt footer.
 *
 * Callers must wrap this component in `VegaWrappers` (which sets up the
 * `ObservabilityQueryProvider`, `AgentInsightsProvider`, theme, etc.).
 * This component does not self-contain those providers.
 */
const AccountAgentInsightsPanel: FC<Omit<AccountVegaPanelProps, 'children'>> = ({
  agentPromptMutation,
  sending,
  enableAgentResponseSelector,
}) => {
  const { data } = useSuspenseQuery(getProjectsQuery({ query: { limit: 1, sort: 'createdOn' } }));
  const { _id, key } = data.items[0];

  return (
    <VegaProjectProvider projectId={_id} projectKey={key}>
      <AgentInsightsPanel
        nonGenAiSkill
        agentPromptMutation={agentPromptMutation}
        sending={sending}
        enableAgentResponseSelector={enableAgentResponseSelector}
      />
    </VegaProjectProvider>
  );
};

export const AccountVegaPanel: FC<AccountVegaPanelProps> = ({
  enableAgentResponseSelector,
  agentPromptMutation,
  sending,
  children,
}) => {
  return (
    <>
      {children}
      <Box id={O11Y_PORTAL_ID} />
      <QuietErrorBoundary owner="team-o11y">
        <Suspense fallback={null}>
          <AccountAgentInsightsPanel
            enableAgentResponseSelector={enableAgentResponseSelector}
            agentPromptMutation={agentPromptMutation}
            sending={sending}
          />
        </Suspense>
      </QuietErrorBoundary>
    </>
  );
};
